import { makeAutoObservable, runInAction } from 'mobx'
import { api } from '@/shared/api/endpoints'
import type { Currency, DepositAddressResponse } from '@/shared/api/types'
import { explain } from '@/shared/lib/errors'
import type { UiStore } from '@/stores/UiStore'

/**
 * Приём средств: адрес пополнения аккаунта.
 *
 * Суммы здесь нет намеренно, и это не упрощение. Экран «Получить»
 * показывает адрес, а сколько на него отправят, решает плательщик:
 * сколько придёт, столько и зачислится владельцу адреса. Заявка
 * с фиксированной суммой — другая сущность, она живёт на странице
 * оплаты и создаётся через API.
 *
 * Адрес запрашивается один раз на валюту и потом НЕ меняется, пока
 * на него ничего не пришло: мерчант мог показать его плательщику
 * и уйти со страницы. Подменить адрес под уже отправленным переводом
 * значит потерять этот перевод.
 */
export class ReceiveStore {
  currency: Currency | null = null
  details: DepositAddressResponse | null = null
  error: string | null = null
  busy = false

  private readonly ui: UiStore

  constructor(ui: UiStore) {
    this.ui = ui
    makeAutoObservable<this, 'ui'>(this, { ui: false })
  }

  start(currency: Currency): void {
    if (this.currency === currency && this.details !== null) return
    this.currency = currency
    this.details = null
    this.error = null
    void this.load(currency)
  }

  get address(): string {
    return this.details?.address ?? ''
  }

  async load(currency: Currency): Promise<void> {
    if (this.busy) return
    this.busy = true
    this.error = null
    try {
      const details = await api.money.depositAddress(currency)
      runInAction(() => {
        // Пока идёт запрос, человек мог переключить валюту. Ответ
        // на прошлую валюту показывать нельзя: это адрес ДРУГОЙ сети,
        // и перевод по нему уйдёт в никуда.
        if (this.currency === currency) this.details = details
      })
    } catch (cause) {
      const message = explain(cause)
      runInAction(() => { this.error = message })
      this.ui.failure(message)
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  /** `done` — готовая фраза целиком: «Адрес скопирован». */
  async copy(text: string, done: string): Promise<void> {
    if (text === '') return
    try {
      await navigator.clipboard.writeText(text)
      this.ui.success(done)
    } catch {
      // Буфер закрыт политикой браузера — не выдумываем успех:
      // человек решит, что адрес в буфере, и вставит чужой.
      this.ui.failure('Не удалось скопировать')
    }
  }
}
